import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { requestsApi, usersApi, workLogsApi, commentsApi } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import clsx from 'clsx';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const statusFlow = ['OPEN', 'IN_PROGRESS', 'REVIEW', 'DONE', 'CLOSED'];

const statusLabels: Record<string, string> = {
  OPEN: '대기',
  IN_PROGRESS: '진행중',
  REVIEW: '검토',
  DONE: '완료',
  CLOSED: '종료',
};

const priorityLabels: Record<string, string> = {
  P1: 'P1 (긴급)',
  P2: 'P2 (중요)',
  P3: 'P3 (일반)',
};

const rateLabels: Record<string, { label: string; color: string }> = {
  NORMAL: { label: '일반', color: 'text-gray-600' },
  URGENT_NIGHT: { label: '야간긴급 (1.5x)', color: 'text-orange-600' },
  URGENT_HOLIDAY: { label: '휴일긴급 (2.0x)', color: 'text-red-600' },
};

export default function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [newComment, setNewComment] = useState('');
  const [showLogForm, setShowLogForm] = useState(false);
  const [logData, setLogData] = useState({
    hours: 0.1,
    workDate: new Date().toISOString().split('T')[0],
    rateType: 'NORMAL',
    action: '',
    note: '',
  });

  // 작업기록 수정 상태
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editLogData, setEditLogData] = useState({
    hours: 0.1,
    workDate: '',
    rateType: 'NORMAL',
    action: '',
    note: '',
  });

  // 코멘트 수정 상태
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');

  // 사용자 목록 (담당자 배정용)
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });
  const users = usersData?.data || [];

  // 요청 상세
  const { data: requestData, isLoading } = useQuery({
    queryKey: ['request', id],
    queryFn: () => requestsApi.get(id!),
    enabled: !!id,
  });

  // 작업 기록
  const { data: logsData } = useQuery({
    queryKey: ['request', id, 'logs'],
    queryFn: () => requestsApi.getLogs(id!),
    enabled: !!id,
  });

  // 코멘트
  const { data: commentsData } = useQuery({
    queryKey: ['request', id, 'comments'],
    queryFn: () => requestsApi.getComments(id!),
    enabled: !!id,
  });

  // 상태 변경
  const statusMutation = useMutation({
    mutationFn: (status: string) => requestsApi.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id] });
    },
  });

  // 코멘트 추가
  const commentMutation = useMutation({
    mutationFn: (content: string) => requestsApi.addComment(id!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id, 'comments'] });
      setNewComment('');
    },
  });

  // 작업 기록 추가
  const logMutation = useMutation({
    mutationFn: (data: any) => requestsApi.addLog(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id, 'logs'] });
      queryClient.invalidateQueries({ queryKey: ['request', id] });
      setShowLogForm(false);
      setLogData({ hours: 0.1, workDate: new Date().toISOString().split('T')[0], rateType: 'NORMAL', action: '', note: '' });
    },
  });

  // 작업 기록 수정
  const updateLogMutation = useMutation({
    mutationFn: ({ logId, data }: { logId: number; data: any }) => workLogsApi.update(logId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id, 'logs'] });
      queryClient.invalidateQueries({ queryKey: ['request', id] });
      setEditingLogId(null);
    },
  });

  // 작업 기록 삭제
  const deleteLogMutation = useMutation({
    mutationFn: (logId: number) => workLogsApi.delete(logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id, 'logs'] });
      queryClient.invalidateQueries({ queryKey: ['request', id] });
    },
  });

  // 코멘트 수정
  const updateCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: number; content: string }) =>
      commentsApi.update(commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id, 'comments'] });
      setEditingCommentId(null);
    },
  });

  // 코멘트 삭제
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => commentsApi.delete(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id, 'comments'] });
    },
  });

  // 담당자 변경
  const assigneeMutation = useMutation({
    mutationFn: (assigneeId: number | null) => requestsApi.update(id!, { assigneeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id] });
    },
  });

  // 삭제
  const deleteMutation = useMutation({
    mutationFn: () => requestsApi.delete(id!),
    onSuccess: () => {
      navigate('/requests');
    },
  });

  // 복사
  const duplicateMutation = useMutation({
    mutationFn: () => requestsApi.duplicate(id!),
    onSuccess: (response) => {
      navigate(`/requests/${response.data?.id}`);
    },
  });

  // 견적 상태 변경
  const quoteMutation = useMutation({
    mutationFn: (quoteStatus: string) => requestsApi.updateQuoteStatus(id!, quoteStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id] });
    },
  });

  const request = requestData?.data;
  const logs = logsData?.data || [];
  const comments = commentsData?.data || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩중...</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-gray-500">요청을 찾을 수 없습니다</div>
        <Link to="/requests" className="btn-secondary">
          목록으로
        </Link>
      </div>
    );
  }

  const handleStatusChange = (status: string) => {
    if (confirm(`상태를 "${statusLabels[status]}"로 변경하시겠습니까?`)) {
      statusMutation.mutate(status);
    }
  };

  const handleDelete = () => {
    if (confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      deleteMutation.mutate();
    }
  };

  const handleDuplicate = () => {
    if (confirm('이 요청을 복사하시겠습니까?')) {
      duplicateMutation.mutate();
    }
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      commentMutation.mutate(newComment.trim());
    }
  };

  const handleLogSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (logData.action.trim()) {
      logMutation.mutate(logData);
    }
  };

  // 작업기록 수정 시작
  const handleEditLog = (log: any) => {
    setEditingLogId(log.id);
    setEditLogData({
      hours: log.hours,
      workDate: log.workDate,
      rateType: log.rateType,
      action: log.action,
      note: log.note || '',
    });
  };

  // 작업기록 수정 저장
  const handleUpdateLog = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingLogId && editLogData.action.trim()) {
      updateLogMutation.mutate({ logId: editingLogId, data: editLogData });
    }
  };

  // 작업기록 삭제
  const handleDeleteLog = (logId: number) => {
    if (confirm('정말 삭제하시겠습니까?')) {
      deleteLogMutation.mutate(logId);
    }
  };

  // 코멘트 수정 시작
  const handleEditComment = (comment: any) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
  };

  // 코멘트 수정 저장
  const handleUpdateComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCommentId && editCommentContent.trim()) {
      updateCommentMutation.mutate({ commentId: editingCommentId, content: editCommentContent.trim() });
    }
  };

  // 코멘트 삭제
  const handleDeleteComment = (commentId: number) => {
    if (confirm('정말 삭제하시겠습니까?')) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  // 수정/삭제 권한 확인 (본인 또는 관리자)
  const canEditLog = (log: any) => user?.id === log.userId || user?.role === 'ADMIN';
  const canEditComment = (comment: any) => user?.id === comment.userId || user?.role === 'ADMIN';

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm text-gray-500">{request.reqId}</span>
            <span className={clsx(
              'px-2 py-0.5 text-xs font-medium rounded-full',
              request.priority === 'P1' && 'bg-red-100 text-red-700',
              request.priority === 'P2' && 'bg-yellow-100 text-yellow-700',
              request.priority === 'P3' && 'bg-green-100 text-green-700',
            )}>
              {priorityLabels[request.priority]}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{request.title}</h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDuplicate}
            className="btn-ghost"
            disabled={duplicateMutation.isPending}
          >
            {duplicateMutation.isPending ? '복사중...' : '복사'}
          </button>
          <Link to={`/requests/${id}/edit`} className="btn-secondary">
            수정
          </Link>
          {user?.role === 'ADMIN' && (
            <button onClick={handleDelete} className="btn-danger">
              삭제
            </button>
          )}
        </div>
      </div>

      {/* 상태 흐름 */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          {statusFlow.map((status, index) => {
            const isCurrent = request.status === status;
            const isPast = statusFlow.indexOf(request.status) > index;
            const isClickable = !isCurrent && request.status !== 'CLOSED';

            return (
              <div key={status} className="flex items-center flex-1">
                <button
                  onClick={() => isClickable && handleStatusChange(status)}
                  disabled={!isClickable}
                  className={clsx(
                    'flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-colors',
                    isCurrent && 'bg-primary-600 text-white',
                    isPast && 'bg-green-500 text-white',
                    !isCurrent && !isPast && 'bg-gray-200 text-gray-500',
                    isClickable && 'hover:bg-primary-500 hover:text-white cursor-pointer',
                    !isClickable && 'cursor-default'
                  )}
                >
                  {isPast ? '✓' : index + 1}
                </button>
                <span className={clsx(
                  'ml-2 text-sm hidden sm:inline',
                  isCurrent && 'font-medium text-primary-600',
                  !isCurrent && 'text-gray-500'
                )}>
                  {statusLabels[status]}
                </span>
                {index < statusFlow.length - 1 && (
                  <div className={clsx(
                    'flex-1 h-1 mx-4',
                    isPast ? 'bg-green-500' : 'bg-gray-200'
                  )} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* 상세 정보 */}
        <div className="md:col-span-2 space-y-6">
          <div className="card p-6">
            <h3 className="font-semibold mb-4">요청 내용</h3>
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap text-gray-700">
                {request.description || '내용 없음'}
              </p>
            </div>

            {request.doneCriteria && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="font-medium text-sm text-gray-500 mb-2">완료 기준</h4>
                <p className="whitespace-pre-wrap text-gray-700">
                  {request.doneCriteria}
                </p>
              </div>
            )}
          </div>

          {/* 작업 기록 */}
          <div className="card">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold">작업 기록</h3>
              <button onClick={() => setShowLogForm(true)} className="btn-secondary text-sm">
                기록 추가
              </button>
            </div>

            {showLogForm && (
              <form onSubmit={handleLogSubmit} className="p-4 border-b border-gray-100 bg-gray-50 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      작업 시간 (시간)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={logData.hours}
                      onChange={(e) => setLogData({ ...logData, hours: parseFloat(e.target.value) })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      작업일
                    </label>
                    <input
                      type="date"
                      value={logData.workDate}
                      onChange={(e) => setLogData({ ...logData, workDate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      요율
                    </label>
                    <select
                      value={logData.rateType}
                      onChange={(e) => setLogData({ ...logData, rateType: e.target.value })}
                      className="input"
                    >
                      <option value="NORMAL">일반 (1.0x)</option>
                      <option value="URGENT_NIGHT">야간긴급 (1.5x)</option>
                      <option value="URGENT_HOLIDAY">휴일긴급 (2.0x)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    작업 내용 *
                  </label>
                  <input
                    type="text"
                    value={logData.action}
                    onChange={(e) => setLogData({ ...logData, action: e.target.value })}
                    className="input"
                    placeholder="수행한 작업 내용"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비고
                  </label>
                  <textarea
                    value={logData.note}
                    onChange={(e) => setLogData({ ...logData, note: e.target.value })}
                    className="input"
                    rows={2}
                    placeholder="추가 메모 (선택)"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowLogForm(false)} className="btn-ghost">
                    취소
                  </button>
                  <button type="submit" className="btn-primary" disabled={logMutation.isPending}>
                    {logMutation.isPending ? '저장중...' : '저장'}
                  </button>
                </div>
              </form>
            )}

            <div className="divide-y divide-gray-100">
              {logs.length === 0 ? (
                <div className="p-4 text-center text-gray-500">작업 기록이 없습니다</div>
              ) : (
                logs.map((log: any) => (
                  <div key={log.id} className="p-4">
                    {editingLogId === log.id ? (
                      // 수정 폼
                      <form onSubmit={handleUpdateLog} className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              작업 시간 (시간)
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              value={editLogData.hours}
                              onChange={(e) => setEditLogData({ ...editLogData, hours: parseFloat(e.target.value) })}
                              className="input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              작업일
                            </label>
                            <input
                              type="date"
                              value={editLogData.workDate}
                              onChange={(e) => setEditLogData({ ...editLogData, workDate: e.target.value })}
                              className="input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              요율
                            </label>
                            <select
                              value={editLogData.rateType}
                              onChange={(e) => setEditLogData({ ...editLogData, rateType: e.target.value })}
                              className="input"
                            >
                              <option value="NORMAL">일반 (1.0x)</option>
                              <option value="URGENT_NIGHT">야간긴급 (1.5x)</option>
                              <option value="URGENT_HOLIDAY">휴일긴급 (2.0x)</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            작업 내용 *
                          </label>
                          <input
                            type="text"
                            value={editLogData.action}
                            onChange={(e) => setEditLogData({ ...editLogData, action: e.target.value })}
                            className="input"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            비고
                          </label>
                          <textarea
                            value={editLogData.note}
                            onChange={(e) => setEditLogData({ ...editLogData, note: e.target.value })}
                            className="input"
                            rows={2}
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => setEditingLogId(null)} className="btn-ghost">
                            취소
                          </button>
                          <button type="submit" className="btn-primary" disabled={updateLogMutation.isPending}>
                            {updateLogMutation.isPending ? '저장중...' : '저장'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      // 기존 표시
                      <>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{log.action}</p>
                            {log.note && <p className="text-sm text-gray-500 mt-1">{log.note}</p>}
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-primary-600">{log.hours}h</span>
                              {log.rateType !== 'NORMAL' && (
                                <span className={clsx('text-xs', rateLabels[log.rateType]?.color)}>
                                  {rateLabels[log.rateType]?.label}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">{log.workDate}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-400">{log.user?.name}</p>
                          {canEditLog(log) && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEditLog(log)}
                                className="text-xs text-gray-500 hover:text-primary-600"
                              >
                                수정
                              </button>
                              <button
                                onClick={() => handleDeleteLog(log.id)}
                                className="text-xs text-gray-500 hover:text-red-600"
                                disabled={deleteLogMutation.isPending}
                              >
                                삭제
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 코멘트 */}
          <div className="card">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold">코멘트</h3>
            </div>

            <div className="divide-y divide-gray-100">
              {comments.map((comment: any) => (
                <div key={comment.id} className="p-4">
                  {editingCommentId === comment.id ? (
                    // 수정 폼
                    <form onSubmit={handleUpdateComment} className="space-y-2">
                      <textarea
                        value={editCommentContent}
                        onChange={(e) => setEditCommentContent(e.target.value)}
                        className="input w-full"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setEditingCommentId(null)} className="btn-ghost text-sm">
                          취소
                        </button>
                        <button type="submit" className="btn-primary text-sm" disabled={updateCommentMutation.isPending}>
                          {updateCommentMutation.isPending ? '저장중...' : '저장'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    // 기존 표시
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-medium">
                        {comment.user?.name?.[0]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{comment.user?.name}</span>
                            <span className="text-xs text-gray-400">
                              {format(new Date(comment.createdAt), 'PPP p', { locale: ko })}
                            </span>
                          </div>
                          {canEditComment(comment) && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEditComment(comment)}
                                className="text-xs text-gray-500 hover:text-primary-600"
                              >
                                수정
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-xs text-gray-500 hover:text-red-600"
                                disabled={deleteCommentMutation.isPending}
                              >
                                삭제
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="mt-1 text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <form onSubmit={handleCommentSubmit} className="p-4 border-t border-gray-100">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="input"
                rows={2}
                placeholder="코멘트 작성..."
              />
              <div className="flex justify-end mt-2">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!newComment.trim() || commentMutation.isPending}
                >
                  {commentMutation.isPending ? '등록중...' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* 사이드바 */}
        <div className="space-y-6">
          <div className="card p-4">
            <h3 className="font-semibold mb-4">정보</h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">고객</dt>
                <dd className="font-medium">{request.client}</dd>
              </div>
              <div>
                <dt className="text-gray-500">채널</dt>
                <dd>{request.channel}</dd>
              </div>
              <div>
                <dt className="text-gray-500">유형</dt>
                <dd>{request.type}</dd>
              </div>
              <div>
                <dt className="text-gray-500">담당자</dt>
                <dd>
                  <select
                    value={request.assigneeId || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      assigneeMutation.mutate(value ? parseInt(value) : null);
                    }}
                    className="input text-sm py-1"
                    disabled={assigneeMutation.isPending}
                  >
                    <option value="">미배정</option>
                    {users.map((u: any) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">등록자</dt>
                <dd>{request.creator?.name}</dd>
              </div>
              <div>
                <dt className="text-gray-500">등록일</dt>
                <dd>{format(new Date(request.createdAt), 'PPP', { locale: ko })}</dd>
              </div>
            </dl>
          </div>

          <div className="card p-4">
            <h3 className="font-semibold mb-4">시간</h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">예상</dt>
                <dd className="font-medium">
                  {request.estimatedHours ? `${request.estimatedHours}h` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">실제</dt>
                <dd className="font-medium text-primary-600">{request.actualHours}h</dd>
              </div>
              {request.requiresQuote && (
                <div>
                  <dt className="text-gray-500">견적</dt>
                  <dd>
                    <select
                      value={request.quoteStatus}
                      onChange={(e) => quoteMutation.mutate(e.target.value)}
                      className={clsx(
                        'text-xs py-1 px-2 rounded-full border-0',
                        request.quoteStatus === 'APPROVED' && 'bg-green-100 text-green-700',
                        request.quoteStatus === 'PENDING' && 'bg-yellow-100 text-yellow-700',
                        request.quoteStatus === 'REJECTED' && 'bg-red-100 text-red-700',
                        request.quoteStatus === 'NONE' && 'bg-gray-100 text-gray-700',
                      )}
                      disabled={quoteMutation.isPending}
                    >
                      <option value="NONE">미요청</option>
                      <option value="PENDING">대기</option>
                      <option value="APPROVED">승인</option>
                      <option value="REJECTED">거절</option>
                    </select>
                  </dd>
                </div>
              )}
              {request.warrantyUntil && (
                <div>
                  <dt className="text-gray-500">보증기간</dt>
                  <dd className={clsx(
                    'font-medium',
                    new Date(request.warrantyUntil) < new Date() ? 'text-gray-400' : 'text-blue-600'
                  )}>
                    ~{format(new Date(request.warrantyUntil), 'yyyy-MM-dd')}
                    {new Date(request.warrantyUntil) >= new Date() && (
                      <span className="ml-1 text-xs">(유효)</span>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
